/**
 * 🎯 **UNIFIED FESTIVAL REVIEW DATA TRANSFORMATION**
 *
 * Eliminates inconsistent data format issues between tRPC (camelCase) and components (snake_case).
 * Creates a single source of truth for festival review data transformation.
 */

// tRPC camelCase format (from database via Drizzle)
export interface TrpcFestivalReview {
	id: string;
	userId: string;
	festivalId: string;
	overallRating: number;
	vibeRating?: number;
	productionRating?: number;
	logisticsRating?: number;
	foodRating?: number;
	content: string | null;
	createdAt: string;
	updatedAt: string;
}

// Component snake_case format (what UI components expect)
export interface DbFestivalReview {
	id?: string;
	festival_id: string;
	user_id: string;
	overall_rating: number;
	vibe_rating?: number;
	production_rating?: number;
	logistics_rating?: number;
	food_rating?: number;
	content: string;
	updated_at?: string;
}

/**
 * Transform tRPC camelCase review data to component snake_case format
 * This ensures consistent data format across all UI components
 */
export function transformTrpcFestivalReviewToDbFormat(
	review: TrpcFestivalReview | null,
): DbFestivalReview | null {
	if (!review) return null;

	return {
		id: review.id,
		festival_id: review.festivalId,
		user_id: review.userId,
		overall_rating: review.overallRating,
		vibe_rating: review.vibeRating,
		production_rating: review.productionRating,
		logistics_rating: review.logisticsRating,
		food_rating: review.foodRating,
		content: review.content || "",
		updated_at: review.updatedAt,
	};
}

/**
 * Check if a festival review has any meaningful data
 * Used to determine if review should be displayed or deleted
 */
export function hasFestivalReviewData(
	review: TrpcFestivalReview | DbFestivalReview | null,
): boolean {
	if (!review) return false;

	// Handle both camelCase and snake_case formats
	const overallRating =
		"overallRating" in review
			? review.overallRating
			: "overall_rating" in review
				? review.overall_rating
				: 0;
	const vibeRating =
		"vibeRating" in review
			? review.vibeRating
			: "vibe_rating" in review
				? review.vibe_rating
				: 0;
	const productionRating =
		"productionRating" in review
			? review.productionRating
			: "production_rating" in review
				? review.production_rating
				: 0;
	const logisticsRating =
		"logisticsRating" in review
			? review.logisticsRating
			: "logistics_rating" in review
				? review.logistics_rating
				: 0;
	const foodRating =
		"foodRating" in review
			? review.foodRating
			: "food_rating" in review
				? review.food_rating
				: 0;
	const content = "content" in review ? review.content : null;

	return (
		(overallRating && overallRating > 0) ||
		(vibeRating && vibeRating > 0) ||
		(productionRating && productionRating > 0) ||
		(logisticsRating && logisticsRating > 0) ||
		(foodRating && foodRating > 0) ||
		(content !== null && content.trim().length > 0)
	);
}

/**
 * Extract modal props from review data (consistent format for modal)
 */
export function extractModalPropsFromFestivalReview(
	review: TrpcFestivalReview | null,
) {
	if (!review) return null;

	return {
		content: review.content || "",
		overallRating: review.overallRating,
		vibeRating: review.vibeRating,
		productionRating: review.productionRating,
		logisticsRating: review.logisticsRating,
		foodRating: review.foodRating,
	};
}

/**
 * Transform FestivalReviewData (local state format) to TrpcFestivalReview format (for display components)
 * This is used when displaying local state in components that expect TrpcFestivalReview format
 */
export function transformFestivalReviewDataToTrpcFormat(
	reviewData: {
		festivalId: string;
		overallRating: number;
		vibeRating: number;
		foodRating: number;
		logisticsRating: number;
		productionRating: number;
		content: string;
	} | null,
	userId: string,
	festivalId: string,
): TrpcFestivalReview | null {
	if (!reviewData) return null;

	// Convert -2 (cleared rating) to 0 for display consistency
	const normalizeRating = (rating: number) => (rating === -2 ? 0 : rating);

	return {
		id: "local", // Temporary ID for local state
		userId,
		festivalId,
		overallRating: normalizeRating(reviewData.overallRating ?? 0),
		vibeRating: normalizeRating(reviewData.vibeRating ?? 0),
		productionRating: normalizeRating(reviewData.productionRating ?? 0),
		logisticsRating: normalizeRating(reviewData.logisticsRating ?? 0),
		foodRating: normalizeRating(reviewData.foodRating ?? 0),
		content: reviewData.content || null,
		createdAt: new Date().toISOString(),
		updatedAt: new Date().toISOString(),
	};
}
